import { UUID } from 'angular2-uuid';
import { ActivatedRoute } from '@angular/router';
import { LoopBackConfig } from './../../shared/sdk/lb.config';
import { RotationItem } from './../../shared/sdk/models/RotationItem';
import { FireLoop } from '../../shared/sdk/models/FireLoop';
import { CategoryRotation } from '../../shared/sdk';
import { ScreenCategory } from './../../shared/sdk/models/ScreenCategory';
import { PlayerGroup } from './../../shared/sdk/models/PlayerGroup';
import { PlayerSerial } from './../../shared/sdk/models/PlayerSerial';
import { LimitedTimeOffer } from './../../shared/sdk/models/LimitedTimeOffer';
import { LoggerService } from '../../shared/sdk';
import { FireLoopRef } from '../../shared/sdk';
import { Player } from './../../shared/sdk/models/Player';
import { Screen } from './../../shared/sdk/models/Screen';
import { RealTime } from './../../shared/sdk/services/core/real.time';
import { Injectable } from '@angular/core';
import { KanvasUserApi } from './../../shared/sdk/services/custom/KanvasUser';
import { KanvasUser } from './../../shared/sdk/models/KanvasUser';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import * as _ from 'lodash';
import { DataURLS } from '../../shared/base.url';


@Injectable()
export class DataService {
  public realtimeConnectionChange$: BehaviorSubject<{
    status: string,
    connected: boolean,
    connecting: boolean,
  }> = new BehaviorSubject({
    status: 'disconnected',
    connected: false,
    connecting: false
  }); // start with not connected status
  public currentUserChange$: BehaviorSubject<KanvasUser> = new BehaviorSubject(null);
  public currentUser: KanvasUser = new KanvasUser(); // dummy starting user info so binding to values in components will work
  public impersonationUser: KanvasUser;
  public isImpersonatingUser: Boolean;
  public userRole: string;
  public effectiveRole: string;
  public childRole: string;
  public effectiveChildRole: string;
  public realtimeStarted = false;
  public id = UUID.UUID();


  // realtime refs
  public kanvasUserRTRef: FireLoopRef<KanvasUser>;
  public playerRTRef: FireLoopRef<Player>;
  public ltoRTRef: FireLoopRef<LimitedTimeOffer>;
  public screenRTRef: FireLoopRef<Screen>;
  public playerSerialRTRef: FireLoopRef<PlayerSerial>;
  public playerGroupRTRef: FireLoopRef<PlayerGroup>;
  public screenCategoryRTRef: FireLoopRef<ScreenCategory>;
  public categoryRotationRTRef: FireLoopRef<CategoryRotation>;
  public rotationItemRTRef: FireLoopRef<RotationItem>;

  private userFilter = {
    include: ['privateLibrary', 'publicLibrary', 'roles', 'profile', {
      relation: 'parentContributor',
      scope: {
        fields: ['publicLibraryId', 'privateLibraryId', 'parentId', 'parentOperator'],
        include: [{
          relation: 'privateLibrary',
          scope: {
            fields: ['id']
          }
        },
        {
          relation: 'publicLibrary',
          scope: {
            fields: ['id']
          }
        }, {
          relation: 'parentOperator', // this will be site admin
          scope: {
            fields: ['publicLibraryId'],
            include: [{
              relation: 'publicLibrary',
              scope: {
                fields: ['id']
              }
            }]
          }
        }
        ]
      }
    }, {
        relation: 'parentOperator',
        scope: {
          fields: ['publicLibraryId', 'privateLibraryId', 'profile', 'roles'],
          include: [{
            relation: 'privateLibrary',
            scope: {
              fields: ['id']
            }
          },
          {
            relation: 'publicLibrary',
            scope: {
              fields: ['id']
            }
          }, {
            relation: 'roles',
          }, 'profile']
        }
      }, {
        relation: 'parentClient',
        scope: {
          fields: ['publicLibraryId', 'privateLibraryId'],
          include: [{
            relation: 'privateLibrary',
            scope: {
              fields: ['id']
            }
          },
          {
            relation: 'publicLibrary',
            scope: {
              fields: ['id']
            }
          }]
        }
      }
    ]
  };

  constructor(
    public logger: LoggerService,
    public kanvasUserApi: KanvasUserApi,
    public realTime: RealTime
  ) {

    LoopBackConfig.setApiVersion(DataURLS.API_VERSION);
    LoopBackConfig.setBaseURL(DataURLS.BASE_URL);

    // start realtime connection
    // this.createRealtimeRefs();

  }
  connectRealtime() {
    if (!this.realtimeStarted) {
      this.createRealtimeRefs();
      this.realtimeStarted = true;
    }
  }

  disconnectRealtime() {
    this.realtimeStarted = false;

    if (this.realTime.connection.isConnected()) {
      this.realTime.connection.disconnect();

    }
    this.realtimeConnectionChange$.next({ status: 'disconnected', connected: false, connecting: true });
  }

  async createRealtimeRefs() {
    this.realtimeConnectionChange$.next({ status: 'connecting', connected: false, connecting: true });

    const that = this;


    this.realTime.onReady().take(1).subscribe((status: string) => {
      // crate refs
      this.kanvasUserRTRef = this.realTime.FireLoop.ref<KanvasUser>(KanvasUser);
      this.playerRTRef = this.realTime.FireLoop.ref<Player>(Player);
      this.ltoRTRef = this.realTime.FireLoop.ref<LimitedTimeOffer>(LimitedTimeOffer);
      this.screenRTRef = this.realTime.FireLoop.ref<Screen>(Screen);
      this.playerSerialRTRef = this.realTime.FireLoop.ref<PlayerSerial>(PlayerSerial);
      this.playerGroupRTRef = this.realTime.FireLoop.ref<PlayerGroup>(PlayerGroup);
      this.screenCategoryRTRef = this.realTime.FireLoop.ref<ScreenCategory>(ScreenCategory);
      this.categoryRotationRTRef = this.realTime.FireLoop.ref<CategoryRotation>(CategoryRotation);
      this.rotationItemRTRef = this.realTime.FireLoop.ref<RotationItem>(RotationItem);

      // FOR TESTING: should be able to query data at this point
      //  const ltoData = await this.ltoApi.findById<LimitedTimeOffer>(this.elementConfig.ltoId, { include: 'offerCodes' }).toPromise();
      // if (!this.kanvasUserApi.isAuthenticated()) {
      //   this.logger.info(`Realtime authenticated... with userid: ${this.currentUser.id}`);
      // } else {
      //   this.logger.info(`Realtime started unauthenticated...`);
      // }

      if (this.realTime.connection.authenticated) {
        this.logger.info(`Realtime authenticated... with userid: ${this.currentUser.id}`);
        that.realtimeConnectionChange$.next({ status: 'connected', connected: true, connecting: false });
      } else {
        this.logger.info(`Realtime has started unauthenticated...`);
      }

      this.realTime.onAuthenticated().take(1).subscribe((msg: any) => {
        this.logger.info(`Realtime connected and authenticated...`);
        that.realtimeConnectionChange$.next({ status: 'connected', connected: true, connecting: false });
      });

      this.realTime.onUnAuthorized().take(1).subscribe(msg => {
        this.logger.info(`Realtime not authorized to perform action... ${msg}`);
      });

      this.realTime.onDisconnect().take(1).subscribe(msg => {
        that.realtimeConnectionChange$.next({ status: 'disconnected', connected: false, connecting: true });
        this.logger.info('Realtime disconnected from server!');
        this.logger.info('Trying to reconnnect now...');
        this.createRealtimeRefs();
      });


    });
  }

  async impersonateUser(id: string) {
    this.impersonationUser = <KanvasUser>await this.kanvasUserApi.findById(id, this.userFilter).toPromise(); // set temp user
    await this.setCurrentUser();  // sets the current user
    this.isImpersonatingUser = true;
  }

  async clearImpersonation() {
    this.impersonationUser = null;
    this.isImpersonatingUser = false;

    // set user again
    await this.setCurrentUser();  // sets the current user
  }

  getCurrentUser(): KanvasUser {
    // return impsersonated user, or return actual user
    // if (!this.currentUser.id) {
    //   await this.setCurrentUser(); // set if empty
    // }

    return this.currentUser;
  }

  async setCurrentUser(): Promise<KanvasUser> {
    let tempUser: KanvasUser;
    let changed: boolean;

    try {
      if (this.impersonationUser) {
        tempUser = this.impersonationUser;
      } else {
        if (this.kanvasUserApi.getCurrentId()) {
          // get current user
          tempUser = await this.kanvasUserApi.getCurrent(this.userFilter).toPromise();
        } else {
          // user is logged out.. clear everything
          tempUser = new KanvasUser();
        }

      }

      if (tempUser.id !== this.currentUser.id) {
        changed = true;
      }

      this.currentUser = tempUser;

      this.userRole = await this.getCurrentRole();


      if (this.userRole === 'SubUser') {
        this.effectiveRole = _.get(this.currentUser.parentOperator, 'roles[0].name', 'unknown');
      } else {
        this.effectiveRole = this.userRole;
      }

      this.effectiveChildRole = await this.getEffectiveChildRole();





      this.childRole = await this.getChildRole();

      if (changed) {
        this.currentUserChange$.next(this.currentUser);
      }

      return this.currentUser;
    } catch (error) {
      this.logger.error('could not set current user!', error);
      throw error; // rethrow
    }
  }

  getCalculatedUserId(): string {
    if (this.impersonationUser) {
      if (this.userRole === 'SubUser') {
        return this.impersonationUser.parentOperator.id;
      } else {
        return this.impersonationUser.id;
      }

    } else {
      if (this.currentUser && this.userRole === 'SubUser') {
        return this.currentUser.parentOperator.id;
      } else {
        return this.kanvasUserApi.getCurrentId();
      }

    }
  }

  async getAssoiatedDistributorId(): Promise<string> {
    const rolename = await this.getCurrentRole();
    const curUser = this.getCurrentUser();

    if (rolename === 'Contributor') {
      return curUser.id;
    } else {
      return curUser.parentContributorId;
    }
  }

  async getHighestOwnerId(): Promise<string> {
    const rolename = await this.getCurrentRole();
    const curUser = this.getCurrentUser();

    // return self
    if (rolename === 'SiteAdmin') {
      return curUser.id;
    }

    // return self
    if (rolename === 'Contributor') {
      return curUser.id;
    }

    // return Distribuor
    if (['Operator', 'SubUser', 'Client'].indexOf(rolename) > -1) {
      return curUser.parentContributorId;
    }
  }

  async getCurrentUserPublicLibId() {
    const rolename = await this.getCurrentRole();
    const curUser = this.getCurrentUser();

    if (rolename === 'SubUser') {
      return this.currentUser.parentOperator.publicLibrary.id;
    } else {
      return this.currentUser.publicLibrary.id;
    }
  }

  getCurrentUserPrivateLibId() {
    if (this.userRole === 'SubUser') {
      return this.currentUser.parentOperator.privateLibrary.id;
    } else {
      return this.currentUser.privateLibrary.id;
    }
  }

  async getSiteAdminPublicLibraryId() {
    const rolename = await this.getCurrentRole();
    const curUser = this.getCurrentUser();

    // return self
    if (rolename === 'SiteAdmin') {
      return curUser.publicLibrary.id;
    }

    // return parent
    if (rolename === 'Distributor') {
      return curUser.parentOperator.publicLibrary.id;
    }

    // return Distribuor's parent
    if (['User', 'SubUser', 'Client'].indexOf(rolename) > -1) {
      return curUser.parentContributor.parentOperator.publicLibrary.id;
    }
  }

  async getCurrentRole(): Promise<string> {
    let role = '';

    if (this.currentUser && this.currentUser.id) {
      role = _.get(this.currentUser, 'roles[0].name', 'unknown');
    }

    return role;
  }

  // return a predefined child role based on the current user role
  async getChildRole(): Promise<string> {
    const userRole = await this.getCurrentRole();
    let childRole = '';

    if (userRole === 'SiteAdmin') { childRole = 'Contributor'; }
    if (userRole === 'Contributor') { childRole = 'Client'; }
    if (userRole === 'Client') { childRole = 'Operator'; }
    if (userRole === 'Operator') { childRole = 'SubUser'; }
    if (userRole === 'SubUser') { childRole = ''; }
    if (userRole === 'Guest') { childRole = ''; }

    return childRole;
  }

  async getEffectiveChildRole(): Promise<string> {
    const userRole = this.effectiveRole;
    let childRole = '';

    if (userRole === 'SiteAdmin') { childRole = 'Contributor'; }
    if (userRole === 'Contributor') { childRole = 'Client'; }
    if (userRole === 'Client') { childRole = 'Operator'; }
    if (userRole === 'Operator') { childRole = 'SubUser'; }
    if (userRole === 'SubUser') { childRole = ''; }
    if (userRole === 'Guest') { childRole = ''; }

    return childRole;
  }

}



// WEBPACK FOOTER //
// ./src/app/core.module/services/data.service.ts